import {
  Badge,
  Button,
  Card,
  CardHeader,
  CardFooter,
  Table,
  Container,
  Row,
  Modal,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Input,
  Label,
  UncontrolledDropdown,
  DropdownMenu,
  DropdownItem,
  DropdownToggle,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap";
import { useEffect, useState, useMemo } from "react";
import Header from "components/Headers/Header.js";
import Loader from "components/Loader.js"; // ✅
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

const TableSchedules = () => {
  const [schedules, setSchedules] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true); // ✅
  const [newSchedule, setNewSchedule] = useState({
    id: null,
    countryId: "",
    times: "",
  });
  const [modalScheduleOpen, setModalScheduleOpen] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [schedulesPerPage] = useState(10);
  const [sortOrder, setSortOrder] = useState("asc");

  const fetchSchedules = async () => {
    try {
      const response = await axios.get(`${API_URL}/schedules`);
      setSchedules(response.data);
    } catch (error) {
      console.error("Erro ao buscar agendamentos:", error);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${API_URL}/countries`);
      setCountries(response.data);
    } catch (error) {
      console.error("Erro ao buscar países:", error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        await Promise.all([fetchSchedules(), fetchCountries()]);
      } finally {
        setLoading(false); // ✅ desativa o loader após carregar
      }
    };

    loadData();
  }, []);

  const toggleScheduleModal = () => {
    setModalScheduleOpen(!modalScheduleOpen);
    if (!modalScheduleOpen) {
      setNewSchedule({ id: null, countryId: "", times: "" });
    }
  };

  const handleScheduleInputChange = (e) => {
    const { name, value } = e.target;
    setNewSchedule({ ...newSchedule, [name]: value });
  };

  const handleCreateSchedule = async () => {
    try {
      const payload = {
        countryId: Number(newSchedule.countryId),
        times: newSchedule.times.split(",").map((t) => t.trim()),
      };

      if (newSchedule.id) {
        await axios.put(`${API_URL}/schedules/${newSchedule.id}`, payload);
        fetchSchedules();
      } else {
        const response = await axios.post(`${API_URL}/schedules`, payload);
        setSchedules([...schedules, response.data.schedule]);
      }

      setNewSchedule({ id: null, countryId: "", times: "" });
      toggleScheduleModal();
    } catch (error) {
      console.error("Erro ao criar ou editar agendamento:", error);
    }
  };

  const handleEditSchedule = (schedule) => {
    setNewSchedule({
      id: schedule.id,
      countryId: schedule.countryId,
      times: schedule.times.join(", "),
    });
    setModalScheduleOpen(true);
  };

  const handleDeleteSchedule = async (id) => {
    try {
      const numericId = Number(id);
      await axios.delete(`${API_URL}/schedules/${numericId}`);
      setSchedules(schedules.filter((schedule) => schedule.id !== numericId));
    } catch (error) {
      console.error("Erro ao deletar agendamento:", error);
      alert("Erro: não é possível excluir este agendamento.");
    }
  };

  const handleDeleteSingleTime = async (scheduleId, timeToDelete) => {
    const numericId = Number(scheduleId);
    const schedule = schedules.find((s) => s.id === numericId);
    if (!schedule) return alert("Agendamento não encontrado.");

    const updatedTimes = schedule.times.filter((time) => time !== timeToDelete);
    try {
      await axios.put(`${API_URL}/schedules/${numericId}`, {
        countryId: schedule.countryId,
        times: updatedTimes,
      });
      fetchSchedules();
    } catch (error) {
      console.error("Erro ao remover horário:", error);
    }
  };

  const getCountryLabel = (id) => {
    const country = countries.find((c) => c.id === id);
    return country ? `${country.name} (${country.code})` : "-";
  };

  const sortedSchedules = useMemo(() => {
    return [...schedules].sort((a, b) => {
      const nameA = getCountryLabel(a.countryId).toLowerCase();
      const nameB = getCountryLabel(b.countryId).toLowerCase();
      return sortOrder === "asc" ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
    });
  }, [schedules, sortOrder]);

  const indexOfLast = currentPage * schedulesPerPage;
  const indexOfFirst = indexOfLast - schedulesPerPage;
  const currentSchedules = sortedSchedules.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(sortedSchedules.length / schedulesPerPage);

  const handlePageChange = (e, pageNumber) => {
    e.preventDefault();
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  // ✅ Exibe loader se ainda estiver carregando
  if (loading) {
    return (
      <>
        <Header />
        <Loader />
      </>
    );
  }

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <Row>
          <div className="col">
            <Card className="shadow">
              <CardHeader className="border-0 d-flex justify-content-between align-items-center">
                <h3 className="mb-0">Agendamentos por País</h3>
                <Button color="danger" onClick={toggleScheduleModal}>
                  Novo Agendamento
                </Button>
              </CardHeader>
              <Table className="align-items-center table-flush" responsive>
                <thead className="thead-light">
                  <tr>
                    <th
                      scope="col"
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        setSortOrder(sortOrder === "asc" ? "desc" : "asc")
                      }
                    >
                      País {sortOrder === "asc" ? "▲" : "▼"}
                    </th>
                    <th scope="col">Horários</th>
                    <th scope="col" className="text-right">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentSchedules.map((schedule) => (
                    <tr key={schedule.id}>
                      <td>{getCountryLabel(schedule.countryId)}</td>
                      <td>
                        {Array.isArray(schedule.times) ? (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                            {schedule.times.map((time, idx) => (
                              <Badge
                                key={idx}
                                color="info"
                                pill
                                style={{ cursor: "pointer" }}
                                onClick={() => handleDeleteSingleTime(schedule.id, time)}
                              >
                                {time} ×
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="text-right">
                        <UncontrolledDropdown>
                          <DropdownToggle
                            className="btn-icon-only text-light"
                            href="#"
                            role="button"
                            size="sm"
                            color=""
                            onClick={(e) => e.preventDefault()}
                          >
                            <i className="fas fa-ellipsis-v" />
                          </DropdownToggle>
                          <DropdownMenu right>
                            <DropdownItem onClick={() => handleEditSchedule(schedule)}>
                              Editar
                            </DropdownItem>
                            <DropdownItem onClick={() => handleDeleteSchedule(schedule.id)}>
                              Deletar
                            </DropdownItem>
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <CardFooter className="py-4">
                <Pagination className="pagination justify-content-end mb-0">
                  <PaginationItem disabled={currentPage === 1}>
                    <PaginationLink
                      href="#prev"
                      onClick={(e) => handlePageChange(e, currentPage - 1)}
                    >
                      <i className="fas fa-angle-left" />
                    </PaginationLink>
                  </PaginationItem>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <PaginationItem key={page} active={page === currentPage}>
                      <PaginationLink href="#page" onClick={(e) => handlePageChange(e, page)}>
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  ))}
                  <PaginationItem disabled={currentPage === totalPages}>
                    <PaginationLink
                      href="#next"
                      onClick={(e) => handlePageChange(e, currentPage + 1)}
                    >
                      <i className="fas fa-angle-right" />
                    </PaginationLink>
                  </PaginationItem>
                </Pagination>
              </CardFooter>
            </Card>
          </div>
        </Row>
      </Container>

      {/* Modal */}
      <Modal isOpen={modalScheduleOpen} toggle={toggleScheduleModal}>
        <div className="modal-header">
          <h5 className="modal-title">
            {newSchedule.id ? "Editar Agendamento" : "Criar Novo Agendamento"}
          </h5>
          <button className="close" type="button" onClick={toggleScheduleModal}>
            <span aria-hidden={true}>×</span>
          </button>
        </div>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label for="countryId">País</Label>
              <Input
                type="select"
                id="countryId"
                name="countryId"
                value={newSchedule.countryId}
                onChange={handleScheduleInputChange}
              >
                <option value="">Selecione um país</option>
                {countries.map((country) => (
                  <option key={country.id} value={country.id}>
                    {country.name} ({country.code})
                  </option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup>
              <Label for="times">Horários (separados por vírgula)</Label>
              <Input
                type="text"
                id="times"
                name="times"
                value={newSchedule.times}
                onChange={handleScheduleInputChange}
                placeholder="Ex: 10:00, 14:00, 18:30"
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="success" onClick={handleCreateSchedule}>
            {newSchedule.id ? "Atualizar" : "Criar Agendamento"}
          </Button>
          <Button color="secondary" onClick={toggleScheduleModal}>
            Cancelar
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default TableSchedules;